import _, { cloneDeep } from 'lodash';
import { dateTimeFormatter } from '../../../utilities';
import { formatSecurityMode } from '../../../utilities/MetricFormatter';
import { formatNumber } from '../../../utilities/FormattingUtilities';
import { COLORS, EMPTY } from './VoipTestConstants';

const FONT_FAMILY = 'Roboto';

const LATENCY_THRESHOLD = { good: 300, fair: 600 };
const JITTER_THRESHOLD = { good: 30, fair: 150 };
const LOSS_THRESHOLD = { good: 1, fair: 3 };

function getThemeColors(theme) {
    const isDarkTheme = theme?.palette?.type === 'dark';

    return {
        gaugeBackground: isDarkTheme ? theme.palette.background.default : '#EEE',
        chartBackground: isDarkTheme ? theme.palette.background.paper : '#FFF',
        borderColor: isDarkTheme ? theme.palette.divider : '#EFEFEF',
        titleColor: isDarkTheme ? theme.palette.text.secondary : '#999',
        textColor: theme?.palette?.text?.primary || '#333',
        axisLabelColor: theme?.palette?.text?.primary || '#666',
        tooltipBackground: isDarkTheme ? theme.palette.background.paper : 'rgba(255,255,255,.9)',
        tooltipText: isDarkTheme ? theme.palette.text.primary : '#444',
        valueText: theme?.palette?.text?.primary || 'black',
        statusText: isDarkTheme ? theme.palette.text.secondary : 'silver',
    };
}

const CHARTS = {
    mos: {
        title: { text: 'MOS' },
        series: [
            {
                name: 'MOS',
                type: 'areaspline',
                data: [],
            },
        ],
    },
    loss: {
        title: { text: 'Loss (%)' },
        series: [
            {
                name: 'Loss',
                type: 'areaspline',
                data: [],
            },
        ],
    },
    latency: {
        title: { text: 'Latency (ms)' },
        series: [
            {
                name: 'Latency',
                type: 'areaspline',
                data: [],
            },
        ],
    },
    jitter: {
        title: { text: 'Jitter (ms)' },
        series: [
            {
                name: 'Jitter',
                type: 'areaspline',
                data: [],
            },
        ],
    },
};

/**
 * Gauge chart for highcharts...
 * @param threshold
 * @param COLORS
 * @returns {}
 */
export function getGaugeChartConfig(threshold, pulseName, theme) {
    const colors = getThemeColors(theme);

    return {
        chart: {
            type: 'solidgauge',
            height: 200,
            width: 300,
            backgroundColor: colors.chartBackground,
        },
        title: null,

        pane: {
            center: ['50%', '85%'],
            size: '120%',
            startAngle: -90,
            endAngle: 90,
            background: {
                backgroundColor: colors.gaugeBackground,
                innerRadius: '60%',
                outerRadius: '100%',
                shape: 'arc',
            },
        },

        tooltip: {
            enabled: false,
        },

        // the value axis
        yAxis: [
            {
                // highcharts-ng requires an id property when yAxis is an array
                id: '0',
                lineWidth: 0,
                minorTickInterval: null,
                tickPixelInterval: 400,
                tickWidth: 0,
                title: {
                    y: -70,
                    text: `${pulseName}`,
                    style: {
                        color: colors.axisLabelColor,
                        fontSize: '16px',
                        fontFamily: FONT_FAMILY,
                    },
                },
                labels: {
                    y: 16,
                    style: {
                        color: colors.axisLabelColor,
                        fontFamily: FONT_FAMILY,
                    },
                },
                min: 0,
                max: 5,
                stops: [
                    [parseFloat(threshold.critical) / 5, COLORS.red], // .6
                    [parseFloat(threshold.degraded) / 5, COLORS.yellow], // .8
                    [parseFloat(threshold.degraded) / 5 + 0.001, COLORS.green], // 0.801
                ],
            },
        ],

        plotOptions: {
            solidgauge: {
                dataLabels: {
                    y: 5,
                    borderWidth: 0,
                    useHTML: true,
                },
            },
        },

        credits: {
            enabled: false,
        },

        series: [
            {
                name: 'MOS',
                data: [],
                dataLabels: {
                    formatter() {
                        const value = formatNumber(this.y, 2);
                        return (
                            `<div style="font-family: ${FONT_FAMILY};text-align:center"><span style="font-size:25px;color:${colors.valueText}">${value}</span><br/>` +
                            `<span style="font-size:12px;color:${colors.statusText}"></span>${this.point.status}</div>`
                        );
                    },
                },
            },
        ],
    };
}

export function getChartOptions(aChart, data, theme) {
    if (_.isUndefined(data)) data = [];

    const defaultOptions = getChartDefaults(theme);
    const chartOptions = cloneDeep(CHARTS[aChart]);
    _.first(chartOptions.series).data = data;
    chartOptions.title = _.merge({}, defaultOptions.title, chartOptions.title);

    return _.extend(defaultOptions, chartOptions);
}

function getChartDefaults(theme) {
    const colors = getThemeColors(theme);

    return {
        reflow: true,
        chart: {
            backgroundColor: colors.chartBackground,
            borderColor: colors.borderColor,
            panning: false,
            height: 200,
            spacing: 10,
            style: {
                color: colors.textColor,
                fontFamily: FONT_FAMILY,
            },
        },
        title: {
            align: 'center',
            verticalAlign: 'top',
            y: 5,
            x: 0,
            style: {
                color: colors.textColor,
                fontSize: '18px',
                fontFamily: FONT_FAMILY,
            },
        },
        plotOptions: {
            series: {
                lineWidth: 2,
                shadow: false,
                marker: {
                    enabled: true,
                    radius: 2,
                    states: {
                        hover: {
                            radius: 3,
                        },
                    },
                },
                fillOpacity: 0.5,
            },
        },
        xAxis: {
            type: 'datetime',
            padding: 10,
            title: {
                style: {
                    color: colors.axisLabelColor,
                    fontFamily: FONT_FAMILY,
                },
            },
            labels: {
                format: '{value:%l:%M:%S%p}',
                padding: 10,
                step: 2,
                style: {
                    color: colors.axisLabelColor,
                    fontFamily: FONT_FAMILY,
                },
            },
        },
        yAxis: [
            {
                // highcharts-ng requires an id property when yAxis is an array
                id: '0',
                showLastLabel: true,
                labels: {
                    style: {
                        color: colors.axisLabelColor,
                        fontFamily: FONT_FAMILY,
                    },
                },
                title: {
                    text: null,
                    style: {
                        color: colors.axisLabelColor,
                        fontFamily: FONT_FAMILY,
                    },
                },
            },
        ],
        legend: {
            itemStyle: {
                color: colors.textColor,
                fontFamily: FONT_FAMILY,
            },
            itemHoverStyle: {
                color: colors.textColor,
            },
            itemHiddenStyle: {
                color: colors.statusText,
            },
        },
        noData: {
            style: {
                color: colors.textColor,
                fontFamily: FONT_FAMILY,
            },
        },
        tooltip: {
            enabled: true,
            hideDelay: 0,
            backgroundColor: colors.tooltipBackground,
            style: { color: colors.tooltipText, fontSize: '8pt', padding: '10px', fontFamily: FONT_FAMILY },
            formatter: formatChartTooltip,
        },
        series: [],
        credits: {
            enabled: false,
        },
    };
}

/**
 * Formats the text for the HighCharts tooltips.  Uses the series name, and pulls off the measure in parens
 *   to format the text nicely.
 *
 * @returns {string}
 */
function formatChartTooltip() {
    let label = this.point.series.name;
    const parenPos = label.indexOf(' (');

    if (parenPos !== -1) label = label.substr(0, parenPos);
    const labelValue = formatNumber(this.y, 2);
    return `<strong>${label}: ${labelValue}</strong><br/>${dateTimeFormatter(this.point.x)}`;
}

/**
 * Convert the value of security mode
 * @param mode
 * @returns {String}
 */
export function convertSecurityMode(results, isCallee) {
    const mode =
        isCallee && results.isVoipPulse
            ? _.get(results, 'config.results.callee.avgsFmt.securityMode')
            : _.get(results, 'config.results.caller.avgsFmt.securityMode');

    return formatSecurityMode(mode) || 'No';
}

/**
 * Convert the value of SIP direct calling mode
 * @param mode
 * @returns {String}
 */
export function convertDirectCallingMode(results, isCallee) {
    const agent = isCallee ? _.get(results, 'test.testConfig.callee') : _.get(results, 'test.testConfig.caller');
    const agentId = _.get(agent, 'id');
    const useDirectCalling = _.get(agent, 'voipConfiguration.useDirectCalling', false);
    if (agentId) {
        return useDirectCalling ? 'Yes' : 'No';
    }
    return '--';
}

/**
 * moss is inverted so different function
 * @param threshold
 * @returns {*}
 */
export function mosSquareColor(threshold, value) {
    if (isEmptyValue(value)) {
        return '';
    }

    const classes = 'table-icon fa fa-square';

    if (value > threshold.good) {
        return `${classes} good`;
    }
    if (value <= threshold.good && value > threshold.fair) {
        return `${classes} fair`;
    }
    return `${classes} critical`;
}

/**
 * threshold look like {good: val, fair: val}
 * @param threshold
 * @param value
 */
function determineAvgSquareColor(threshold, value) {
    if (isEmptyValue(value)) {
        return '';
    }

    const classes = 'table-icon fa fa-square';

    if (value < threshold.good) {
        return `${classes} good`;
    }
    if (value >= threshold.good && value < threshold.fair) {
        return `${classes} fair`;
    }
    return `${classes} critical`;
}

/**
 * Shared empty value logic
 * @param value
 * @returns {boolean}
 */
function isEmptyValue(value) {
    return value === -1 || value === undefined || value === EMPTY;
}

export function jitterSquareColor(value) {
    return determineAvgSquareColor(JITTER_THRESHOLD, value);
}

export function lossSquareColor(value) {
    return determineAvgSquareColor(LOSS_THRESHOLD, value);
}

export function latencySquareColor(value) {
    return determineAvgSquareColor(LATENCY_THRESHOLD, value);
}
